import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";
import { object } from "prop-types";

export const getCustomers = createAsyncThunk(
  "customer/getCustomers",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/customers?type=${data.type}&tab=${data.activetab}&page=${data.page}&perPage=${data.perPage}&search=${data.search}&filter=${data.filter}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get customer");
    }
  }
);

export const getCustomerMembers = createAsyncThunk(
  "customer/getCustomerMembers",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/customer/members/${data.id}?type=${data.type}&tab=${data.activetab}&page=${data.page}&perPage=${data.perPage}&search=${data.search}&filter=${data.filter}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get customer member");
    }
  }
);

export const getCompanyMembers = createAsyncThunk(
  "customer/getCompanyMembers",
  async ({ user_id, type = "Customer", search = "" }, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-company-member?user_id=${user_id}&type=${type}&search=${search}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get company members");
    }
  }
);

export const getBusinessType = createAsyncThunk(
  "customer/getBusinessType",
  async (type = 'business', { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/vendor-business/types/${type}`);
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get customer");
    }
  }
);

export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (data, { rejectWithValue }) => {

    try {
      let formData = new FormData();
      let files = [
        "passbook_image",
        "residence_image",
        "address_proof_image",
        "vat_certificate_image",
        "commercial_reg_image"
      ];

      Object.entries(data).forEach(([key, value]) => {
        if (files.includes(key) && !(value instanceof File)) {
          formData.append(key, "");
        }
        else if (key === "other_documents" && Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File) {
              formData.append("other_documents[]", file);
            }
          });
        }
        else {
          formData.append(key, value ?? "");
        }
      });

      let url = "register";
      if (data?.id !== null) {
        url = "update-customer/" + data.id;
      }
      const response = await api.post("admin/" + url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add user");
    }
  }
);


export const deleteOtherDocument = createAsyncThunk(
  "customer/deleteOtherDocument",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/delete-other-doc/" + id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load products");
    }
  }
);

export const bulkActionUpdate = createAsyncThunk(
  "customer/bulkActionUpdate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/bulk-update", data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to perform action");
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  "customer/deleteCustomer",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.del("admin/delete-customer/" + id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete customer");
    }
  }
);

export const approveCustomer = createAsyncThunk(
  "customer/approveCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/approve-customer/" + data.id, { status: data.value });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load products");
    }
  }
);

// for the customer old/new data

export const getProfileUpdateRequest = createAsyncThunk(
  "customer/getProfileUpdateRequest",
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/customers/${customerId}/profile-update-request`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get customer");
    }
  }
);

export const getProfileUpdateRequestAction = createAsyncThunk(
  "customer/getProfileUpdateRequestAction",
  async ({customerId, action }, { rejectWithValue }) => {
    try {
      const response = await api.post(`admin/customers/${customerId}/profile-update-request/action`,
        {
          action,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get customer");
    }
  }
);

const customerSlice = createSlice({
  name: "customer",
  initialState: {
    list_customers: [],
    list_customer_members:[],
    list_company_members: null,
    company_members_loading: false,
    selected: null,
    status: 'idle',
    loading: false,
    list_loading: false,
    // for the old/new data of the customer
    profile_update_request: null,
    business_types: []
  },
  reducers: {
    setSelectedCustomer(state, action) {
      const payload = action.payload || {};
      state.selected = {
        id: payload.id,
        parent_id: payload.parent_id || '',
        first_name: payload.first_name,
        last_name: payload.last_name,
        company_name: payload.company_name,
        profile_image:payload.profile_image,
        business_type: payload.business_type,
        gender: payload.gender,
        email: payload.email,
        mobile_number: payload.mobile_number,
        alternate_number: payload.alternate_number || null,
        alternate_country_code: payload.alternate_country_code || null,
        country_code: payload.country_code || null,
        permanent_address: payload.permanent_address,
        address_proof_image: payload.address_proof_image,
        commercial_reg_number: payload.commercial_reg_number,
        commercial_reg_image: payload.commercial_reg_image,
        vat_certificate_number: payload.vat_certificate_number,
        vat_certificate_image: payload.vat_certificate_image,
        account_number: payload.account_number?.toString() || "",
        passbook_image: payload.passbook_image,
        bank_swift_code: payload.bank_swift_code,
        other_documents: payload.other_documents,
        residence_id: payload.residence_id,
        residence_image: payload.residence_image,
        account_holder_first_name: payload.account_holder_first_name,
        account_holder_last_name: payload.account_holder_last_name,
        role: payload.role,
        other_documents_detail: payload.other_documents_detail,
        vendor_business_type_name: payload.vendor_business_type_name,
        date: payload?.created_at
      };
    },
    resetStatus(state) {
      state.status = "idle";
      state.loading = false;
      state.list_loading = false
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessType.fulfilled, (state, action) => {
        state.business_types = action.payload;
      })
      .addCase(getCustomers.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.list_loading = false;
        state.list_customers = action.payload;
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.list_loading = false;
        state.error = action.payload;
      }).addCase(getCustomerMembers.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(getCustomerMembers.fulfilled, (state, action) => {
        state.list_loading = false;
        state.list_customer_members = action.payload;
      })
      .addCase(getCustomerMembers.rejected, (state, action) => {
        state.list_loading = false;
        state.error = action.payload;
      })
      .addCase(getCompanyMembers.pending, (state) => {
        state.company_members_loading = true;
      })
      .addCase(getCompanyMembers.fulfilled, (state, action) => {
        state.company_members_loading = false;
        state.list_company_members = action.payload;
      })
      .addCase(getCompanyMembers.rejected, (state, action) => {
        state.company_members_loading = false;
        state.list_company_members = null;
        state.error = action.payload;
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(approveCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveCustomer.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(approveCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteOtherDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOtherDocument.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteOtherDocument.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkActionUpdate.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkActionUpdate.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkActionUpdate.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getProfileUpdateRequest.pending, (state) => {
        state.loading = true;
        state.profile_update_request = null;
      })
      .addCase(getProfileUpdateRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.profile_update_request = action.payload?.data || null;
      })
      .addCase(getProfileUpdateRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getProfileUpdateRequestAction.pending, (state) => {
        state.loading = true;
        state.profile_update_request = null;
      })
      .addCase(getProfileUpdateRequestAction.fulfilled, (state, action) => {
        state.loading = false;
        state.profile_update_request = action.payload?.data || null;
      })
      .addCase(getProfileUpdateRequestAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      
  }
});

export const { setSelectedCustomer, resetStatus } = customerSlice.actions;
export default customerSlice.reducer;
